// utils/dateUtils.js

/**
 * Funzione helper per interpretare correttamente le date dal server
 * Rimuove il suffisso 'Z' (UTC) e tratta l'orario come locale italiano
 */
export const parseServerDateTime = (dateTimeString) => {
  if (!dateTimeString) return null;
  
  // Se contiene 'Z' (UTC), rimuovilo e trattalo come ora locale italiana
  const cleanedString = dateTimeString.replace('Z', '');
  return new Date(cleanedString);
};

/**
 * Formatta una data del server in formato italiano
 */
export const formatDate = (dateString) => {
  const date = parseServerDateTime(dateString);
  if (!date) return '';
  
  return date.toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

/**
 * Formatta un orario del server in formato italiano (HH:MM)
 */
export const formatTime = (timeString) => {
  const date = parseServerDateTime(timeString);
  if (!date) return '';
  
  return date.toLocaleTimeString('it-IT', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Calcola i giorni rimanenti da oggi a una data
 */
export const getDaysRemaining = (dateString) => {
  // Estrai solo la parte della data senza orario
  const datePart = dateString.split('T')[0];
  
  // Crea le date senza interpretazione UTC
  const appointmentDate = new Date(datePart + 'T00:00:00'); // Forza mezzanotte locale
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  return Math.floor((appointmentDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

/**
 * Combina data e ora di un appuntamento per l'ordinamento
 */
export const getAppointmentDateTime = (appointment) => {
  const date = parseServerDateTime(appointment.date);
  const time = parseServerDateTime(appointment.time);
  
  if (!date || !time) return new Date();
  
  // Combina data e ora
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    time.getHours(),
    time.getMinutes()
  );
};

/**
 * Formatta data e ora insieme (es: "27 giugno 2025 - 09:30")
 */
export const formatDateTime = (dateString, timeString) => {
  return `${formatDate(dateString)} - ${formatTime(timeString)}`;
};

/**
 * Verifica se una data è oggi
 */
export const isToday = (dateString) => {
  return getDaysRemaining(dateString) === 0;
};

/**
 * Verifica se una data è domani
 */
export const isTomorrow = (dateString) => {
  return getDaysRemaining(dateString) === 1;
};

/**
 * Verifica se una data è ieri
 */
export const isYesterday = (dateString) => {
  return getDaysRemaining(dateString) === -1;
};

/**
 * Ottieni un indicatore di giorno (Oggi, Domani, Ieri, etc.)
 */
export const getDayIndicator = (dateString) => {
  const daysRemaining = getDaysRemaining(dateString);
  
  if (daysRemaining === 0) {
    return '(Oggi!)';
  } else if (daysRemaining === 1) {
    return '(Domani)';
  } else if (daysRemaining === -1) {
    return '(Ieri)';
  } else if (daysRemaining > 1) {
    return `(tra ${daysRemaining} giorni)`;
  } else if (daysRemaining < -1) {
    return `(${Math.abs(daysRemaining)} giorni fa)`;
  }
  
  return '';
};

/**
 * Funzione specifica per i messaggi - gestisce correttamente UTC
 */
export const parseMessageDateTime = (dateTimeString) => {
  if (!dateTimeString) return null;
  
  try {
    // ✅ Gestisce correttamente UTC per i messaggi (mantiene la Z)
    const date = new Date(dateTimeString);
    
    // Verifica che la data sia valida
    if (isNaN(date.getTime())) {
      console.warn('Data messaggio non valida:', dateTimeString);
      return null;
    }
    
    return date;
  } catch (error) {
    console.error('Errore nel parsing della data messaggio:', error, dateTimeString);
    return null;
  }
};

/**
 * Formatta data e ora per i messaggi con timezone corretto
 */
export const formatMessageDateTime = (dateTimeString) => {
  const date = parseMessageDateTime(dateTimeString);
  if (!date) return 'Data non valida';
  
  return date.toLocaleString('it-IT', {
    hour: '2-digit',
    minute: '2-digit',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Europe/Rome'
  });
};